// Event wiring for the preview popup. Per-file header actions, prev/next
// navigation, and drag/resize/fail-safe geometry live in sibling modules
// (header-actions.js, navigation.js, geometry.js); this file keeps the
// orchestrator plus copy-link/open, hover, scroll, and Escape handling.

import { setPreviewState, getPreviewState, PREVIEW_POPUP_HIDE_DELAY } from '../state.js';
import { buildPreviewUrl } from '../content.js';
import { showLinkPopup } from './link-popup.js';
import { showCopyToast } from './toasts.js';
import { setupActionButtons } from './header-actions.js';
import { setupNavigation } from './navigation.js';
import { setupDragFunctionality, setupResizeFunctionality, setupWindowResizeFailsafe } from './geometry.js';

export function setupPreviewPopupEvents(newState, closePopup, setPinned, hidePreviewPopup) {
	const popupElement = newState.previewPopupElement;
	const previewUrl = newState.currentPreviewFile ? buildPreviewUrl(newState.currentPreviewFile.file) : '';

	// Setup close button event
	popupElement.find('.preview-popup__close').on('click', closePopup);

	// Setup copy link button event
	popupElement.find('.preview-popup__copy-link').on('click', async function(e) {
		e.stopPropagation();
		const iframe = popupElement.find('.preview-popup__iframe');
		const currentUrl = iframe.length > 0 ? iframe.attr('src') : previewUrl;

		// For PDF blob URLs, use the original previewUrl instead
		const urlToCopy = currentUrl && currentUrl.startsWith('blob:') ? previewUrl : (currentUrl || previewUrl);

		try {
			// Try modern Clipboard API first
			if (navigator.clipboard && navigator.clipboard.writeText) {
				await navigator.clipboard.writeText(urlToCopy);

				// Visual feedback on button
				const copyButton = $(this);
				copyButton.addClass('preview-popup__copy-link--clicked');
				setTimeout(() => {
					copyButton.removeClass('preview-popup__copy-link--clicked');
				}, 800);

				// Show toast notification
				showCopyToast();
			} else {
				// Fallback for older browsers
				const textArea = document.createElement('textarea');
				textArea.value = urlToCopy;
				textArea.style.position = 'fixed';
				textArea.style.left = '-999999px';
				textArea.style.top = '-999999px';
				document.body.appendChild(textArea);
				textArea.focus();
				textArea.select();

				const success = document.execCommand('copy');
				document.body.removeChild(textArea);

				if (success) {
					// Visual feedback on button
					const copyButton = $(this);
					copyButton.addClass('preview-popup__copy-link--clicked');
					setTimeout(() => {
						copyButton.removeClass('preview-popup__copy-link--clicked');
					}, 800);

					// Show toast notification
					showCopyToast();
				} else {
					throw new Error('execCommand copy failed');
				}
			}
		} catch (err) {
			console.warn('[Preview] Clipboard access blocked or failed, showing link popup:', err);
			// Show popup with link if clipboard access is blocked
			showLinkPopup(urlToCopy);
		}
	});

	// Setup open button event
	popupElement.find('.preview-popup__open').on('click', function(e) {
		e.stopPropagation();
		const iframe = popupElement.find('.preview-popup__iframe');
		const currentUrl = iframe.length > 0 ? iframe.attr('src') : previewUrl;
		if (currentUrl) {
			window.open(currentUrl, '_blank', 'noopener');
		}
	});

	// Per-file header actions (Telegram / Favorite / Download)
	setupActionButtons(popupElement, newState);

	// Prev/next document navigation (arrows + counter + keyboard shortcuts)
	setupNavigation(popupElement, newState);

	// Drag the header to move; custom corner grip to resize
	setupDragFunctionality(popupElement, newState, setPinned);
	setupResizeFunctionality(popupElement);

	// Window-resize fail-safe: keep the popup fully on-screen when the window shrinks
	setupWindowResizeFailsafe(popupElement, newState);

	// Hover, scroll, and keyboard (Escape) handling
	setupHoverEvents(popupElement, hidePreviewPopup);
	setupScrollEvents(popupElement, newState, setPinned);
	setupKeyboardEvents(popupElement, closePopup, newState);
}

function setupHoverEvents(popupElement, hidePreviewPopup) {
	popupElement.on('mouseenter', function() {
		const currentState = getPreviewState();
		currentState.previewPopupHovered = true;
		if (currentState.previewPopupTimeout) {
			clearTimeout(currentState.previewPopupTimeout);
			currentState.previewPopupTimeout = null;
		}
		setPreviewState(currentState);

		// Return focus to preview popup element when mouse enters
		setTimeout(() => {
			try {
				const popupEl = popupElement[0];
				if (popupEl && document.body.contains(popupEl)) {
					popupEl.focus();
				}
			} catch (e) {
				// Ignore focus errors
			}
		}, 10);
	});

	// Keep focus on preview popup element while mouse is over it
	let focusReturnTimeout = null;
	popupElement.on('mousemove.preview-focus', function() {
		if (focusReturnTimeout) clearTimeout(focusReturnTimeout);

		focusReturnTimeout = setTimeout(() => {
			const activeElement = document.activeElement;
			const popupEl = popupElement[0];
			const iframe = popupElement.find('.preview-popup__iframe');

			if (activeElement && activeElement !== popupEl &&
				(activeElement === iframe[0] || popupEl.contains(activeElement))) {
				try {
					if (popupEl && document.body.contains(popupEl)) {
						popupEl.focus();
					}
				} catch (e) {
					// Ignore focus errors
				}
			}
			focusReturnTimeout = null;
		}, 100);
	});

	popupElement.on('mouseleave', function() {
		const currentState = getPreviewState();
		currentState.previewPopupHovered = false;
		setPreviewState(currentState);

		setTimeout(() => {
			const latestState = getPreviewState();
			if (!latestState.previewIconHovered && !latestState.previewPopupPinned && !latestState.previewPopupHovered) {
				if (latestState.previewPopupTimeout) {
					clearTimeout(latestState.previewPopupTimeout);
				}
				latestState.previewPopupTimeout = setTimeout(() => {
					const finalState = getPreviewState();
					if (finalState.previewPopupElement && finalState.previewPopupElement.length) {
						if (!finalState.previewPopupPinned && !finalState.previewPopupHovered && !finalState.previewIconHovered) {
							if (typeof hidePreviewPopup === 'function') {
								hidePreviewPopup();
							}
						}
					}
					finalState.previewPopupTimeout = null;
					setPreviewState(finalState);
				}, PREVIEW_POPUP_HIDE_DELAY);
				setPreviewState(latestState);
			}
		}, 50);
	});
}

function setupScrollEvents(popupElement, newState, setPinned) {
	const previewHeader = popupElement.find('.preview-popup__header');
	previewHeader.off('wheel.preview-scroll').on('wheel.preview-scroll', function(e) {
		e.preventDefault();
		e.stopPropagation();
	});

	const globalWheelHandler = function(e) {
		const state = getPreviewState();
		if (!state.previewPopupElement || !state.previewPopupElement.length) {
			return;
		}

		const popupRect = state.previewPopupElement[0].getBoundingClientRect();
		const mouseX = e.clientX;
		const mouseY = e.clientY;

		const isOverPopup = mouseX >= popupRect.left &&
		                   mouseX <= popupRect.right &&
		                   mouseY >= popupRect.top &&
		                   mouseY <= popupRect.bottom;

		if (isOverPopup) {
			const contentDiv = state.previewPopupElement.find('.preview-popup__content');
			const contentRect = contentDiv.length > 0 ? contentDiv[0].getBoundingClientRect() : null;
			const isOverContent = contentRect &&
				mouseX >= contentRect.left &&
				mouseX <= contentRect.right &&
				mouseY >= contentRect.top &&
				mouseY <= contentRect.bottom;

			if (isOverContent) {
				return;
			}

			const setPinnedFn = state.setPinnedCallback;
			if (setPinnedFn && !state.previewPopupPinned) {
				setPinnedFn(true);
			}
			e.preventDefault();
			e.stopPropagation();
		}
	};

	document.addEventListener('wheel', globalWheelHandler, { capture: true, passive: false });
	newState.globalWheelHandler = globalWheelHandler;
	setPreviewState(newState);
}

function setupKeyboardEvents(popupElement, closePopup, newState) {
	popupElement.on('keydown.preview-popup-escape', function(e) {
		if (e.key === 'Escape') {
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
			closePopup();
		}
	});

	const documentEscapeHandler = function(e) {
		if (e.key !== 'Escape') return;

		const state = getPreviewState();
		if (!state.previewPopupElement || !state.previewPopupElement.length ||
			!document.body.contains(state.previewPopupElement[0])) {
			return;
		}

		const linkPopup = window.jQuery ? window.jQuery('.preview-popup__link-popup') : null;
		const linkPopupVisible = linkPopup && linkPopup.length &&
			linkPopup.hasClass('preview-popup__link-popup--visible');
		if (linkPopupVisible) {
			return;
		}

		const previewVisible = state.previewPopupElement.hasClass('preview-popup-overlay--visible') ||
			state.previewPopupElement.is(':visible');

		if (previewVisible) {
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
			closePopup();
		}
	};

	document.addEventListener('keydown', documentEscapeHandler, true);
	newState.documentEscapeHandler = documentEscapeHandler;
	setPreviewState(newState);
}
